"use client";
import { GoogleAnalytics } from "@next/third-parties/google";
import "./globals.scss";
import ReduxProvider from "./StoreProvider.tsx";
import { usePathname, useRouter } from "next/navigation";
import { hideHeader, showFooter } from "./utils/common.ts";

import Header from "@generic-components/Layout/Header";
import Footer from "@generic-components/Layout/Footer";
import Loader from "./components/generic/Loader/index.tsx";
import { Toaster } from "react-hot-toast";
import { Suspense, useEffect, useState } from "react";
// import CookieConsent from "@app/components/generic/CookieConsent";
import { getCookie, setCookie } from "./utils/cookie.ts";
import { authenticatedRoutes, COOKIE_NAME } from "./components/config.js";
import store from "@app/lib/store";
import {
  setLoggedIn,
  setRole,
  setUserDetails,
} from "./lib/features/login/loginSlice.ts";
import { Poppins } from "./utils/fonts/index.ts";
import { APIURL } from "./config/apiUrl";
import axios from "axios";
import { DOMAIN } from "./config/environment";

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const router = useRouter();
  const SITE_KEY = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY;
  const pathname = usePathname();
  const [loader, setLoader] = useState(false);
  const [shouldShowFooter, setShouldShowFooter] = useState(false);
  const shouldHideHeader = hideHeader(pathname);
  const [isShowConsent, setIsShowConsent] = useState(false);

  useEffect(() => {
    const isLoggedIn = getCookie(COOKIE_NAME.isLoggedIn) || false;

    if (!isLoggedIn && authenticatedRoutes.includes(pathname)) {
      router.replace("/home");
    }
  }, [pathname]);

  const checkHealth = async () => {
    const url = `${DOMAIN}/${APIURL.HEALTH}`;

    return await axios.get(url);
  };

  useEffect(() => {
    setShouldShowFooter(showFooter(pathname));
  }, [pathname]);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENV !== "local") {
      checkHealth()
        .then((res) => {
          if (res.status !== 200) {
            router.push("/maintenance");
          }
        })
        .catch((err) => {
          if (err?.response?.status !== 200) {
            router.push("/maintenance");
          }
        });
    }
  }, [pathname]);

  useEffect(() => {
    const loadReCAPTCHAScript = () => {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
      script.async = true;
      document.body.appendChild(script);
    };

    loadReCAPTCHAScript();
  }, []);

  useEffect(() => {
    const cookieConsentStatus = getCookie(COOKIE_NAME.cookieConsent) || "";
    const isLoggedIn = getCookie(COOKIE_NAME.isLoggedIn);
    const role = getCookie(COOKIE_NAME.role);
    const userDetails = getCookie(COOKIE_NAME.userDetails);

    let parsedIsLoggedIn = false;
    let parsedRole = "";
    let parsedUserDetails = {
      userId: "",
      fullName: "",
      sourceId: "",
      email: "",
      mobile: "",
      nationality: "",
      status: "",
      source: "",
      providerUserId: "",
      isEmailVerified: false,
      isMobileVerified: false,
    };

    try {
      parsedIsLoggedIn = isLoggedIn ? JSON.parse(isLoggedIn) : false;
    } catch (e) {
      console.error("Error parsing isLoggedIn cookie:", e);
    }

    try {
      parsedRole = role ? JSON.parse(role) : "";
    } catch (e) {
      console.error("Error parsing role cookie:", e);
    }

    try {
      parsedUserDetails = userDetails ? JSON.parse(userDetails) : {};
    } catch (e) {
      console.error("Error parsing userDetails cookie:", e);
    }

    store.dispatch(setLoggedIn(Boolean(parsedIsLoggedIn)));
    store.dispatch(setRole(parsedRole || ""));
    store.dispatch(setUserDetails({ ...parsedUserDetails }));
  }, []);

  useEffect(() => {
    const cookieConsentStatus = getCookie(COOKIE_NAME.cookieConsent) || "";
    if (
      (window.location.href?.includes("emaarOneData") &&
        pathname?.includes("sell")) ||
      pathname?.includes("cookie") ||
      pathname?.includes("privacy")
    ) {
      setIsShowConsent(false);
    } else {
      // setIsShowConsent(!Boolean(cookieConsentStatus));
    }
  }, [pathname]);

  useEffect(() => {
    if (isShowConsent) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isShowConsent]);

  const onAccept = () => {
    setCookie(COOKIE_NAME.cookieConsent, "true", true);
    setIsShowConsent(false);
  };

  const onReject = () => {
    // setCookie(COOKIE_NAME.cookieConsent, "true", true);
    // setIsShowConsent(false);
    router.push("/cookie-policy");
  };

  return (
    <ReduxProvider>
      <html lang="en">
        <head>
          <title>VYOM Properties</title>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes"
          ></meta>
          <meta name="description" content="vyom website"></meta>
        </head>
        <body
          className={`${Poppins} overscroll-y-none ${
            isShowConsent ? "blurBackground" : ""
          }`}
          suppressHydrationWarning={true}
        >
          <GoogleAnalytics gaId="GTM-KKWS32XK" />
          <Loader />
          <Toaster position="top-right" />
          {shouldHideHeader && (
            <header className={`homeHeader`}>
              <Suspense>
                <Header />
              </Suspense>
            </header>
          )}
          <main>{children}</main>
          {shouldShowFooter && (
            <footer>
              <Footer />
            </footer>
          )}
        </body>
        {/* {isShowConsent && (
          <CookieConsent
            isShow={isShowConsent}
            onAccept={onAccept}
            onReject={onReject}
          />
        )} */}
      </html>
    </ReduxProvider>
  );
}
